import React from "react";
import { ActionIcon, Box, Skeleton, Stack, Text } from "@mantine/core";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import styles from "./NestedScreen.module.css";

interface NestedScreenProps {
  title: string;
  /** Back chevron (top left): returns to the parent drawer. */
  onBack: () => void;
  /**
   * True until the screen's data is first available: the body shows a
   * skeleton instead of an empty (grey) area. Callers keep it false on later
   * visits when the data is already cached.
   */
  loading?: boolean;
  /**
   * Header actions, right-aligned on the title line (e.g. a "…" menu with
   * the screen's actions).
   */
  actions?: React.ReactNode;
  /** Sticky footer (Cancel / primary actions). */
  footer?: React.ReactNode;
  /** Muted line under the title (e.g. "Update client, service or comments"). */
  subtitle?: string;
  /**
   * False: no slide-in (the screen opened straight from outside the drawer,
   * e.g. the FAB's New walk-in, so there is nothing to slide over).
   */
  animate?: boolean;
  children?: React.ReactNode;
}

const ChevronLeft = DESIGN_ICONS.ChevronLeft;

/**
 * Progressive-disclosure screen inside a CalendarDrawer (design handoff
 * "Drawer rules"): slides in over the drawer (translateX 16px → 0, opacity
 * 0 → 1, 180ms ease), back chevron top left, scrolling body, sticky footer.
 * Render it as the drawer's child while the slot's `nested` names it.
 */
export default function NestedScreen({
  title,
  onBack,
  loading = false,
  actions,
  footer,
  subtitle,
  animate = true,
  children,
}: NestedScreenProps) {
  return (
    <Box
      className={animate ? styles.screen : `${styles.screen} ${styles.still}`}
      role="region"
      aria-label={title}
    >
      <Box className={styles.header}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={32}
          radius="xl"
          onClick={onBack}
          aria-label="Back"
        >
          <ChevronLeft size={18} />
        </ActionIcon>
        {subtitle ? (
          <Box className={styles.titles}>
            <Text className={styles.title}>{title}</Text>
            <Text className={styles.subtitle}>{subtitle}</Text>
          </Box>
        ) : (
          <Text className={styles.title}>{title}</Text>
        )}
        {actions && <Box className={styles.actions}>{actions}</Box>}
      </Box>
      <Box className={styles.body}>
        {loading ? (
          <Stack gap={12} data-testid="nested-screen-skeleton">
            <Skeleton height={44} radius="md" />
            <Skeleton height={44} radius="md" />
            <Skeleton height={44} radius="md" />
          </Stack>
        ) : (
          children
        )}
      </Box>
      {footer && <Box className={styles.footer}>{footer}</Box>}
    </Box>
  );
}
